import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/config";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Video, PlayCircle, CalendarDays } from "lucide-react";

export default function EventsAdminTab() {
  const [events, setEvents] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(blank());

  const load = async () => {
    const { data } = await api.get("/events");
    setEvents(data);
  };
  useEffect(() => { load(); }, []);

  const open = (ev) => {
    setEditingId(ev ? ev.event_id : null);
    setForm(ev ? {
      title: ev.title || "",
      description: ev.description || "",
      starts_at: toLocalInput(ev.starts_at),
      zoom_url: ev.zoom_url || "",
      is_replay: !!ev.is_replay,
    } : blank());
    setShowForm(true);
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      const isoStart = new Date(form.starts_at).toISOString();
      const payload = {
        title: form.title.trim(),
        description: form.description,
        starts_at: isoStart,
        zoom_url: form.zoom_url.trim(),
        is_replay: !!form.is_replay,
      };
      if (editingId) {
        await api.patch(`/admin/events/${editingId}`, payload);
        toast.success("Event updated");
      } else {
        await api.post("/admin/events", payload);
        toast.success("Event created");
      }
      setShowForm(false);
      setEditingId(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Failed");
    }
  };

  const remove = async (ev) => {
    if (!window.confirm(`Delete "${ev.title}"?`)) return;
    try {
      await api.delete(`/admin/events/${ev.event_id}`);
      toast.success("Deleted");
      load();
    } catch { toast.error("Delete failed"); }
  };

  const now = Date.now();
  const upcoming = events.filter((e) => !e.is_replay && new Date(e.starts_at).getTime() >= now);
  const replays = events.filter((e) => e.is_replay || new Date(e.starts_at).getTime() < now);

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-2xl">Live Events & Replays ({events.length})</h2>
          <p className="text-sm text-white/55 mt-1 max-w-2xl">
            Schedule monthly Q&As with Dr. Slava, post replay links, or announce new cohort kick-offs.
          </p>
        </div>
        <Button onClick={() => open(null)} data-testid="new-event-btn"
          className="rounded-full bg-gradient-to-r from-[#FF8A00] to-[#E52E71]">
          <Plus size={14} className="mr-2" /> New event
        </Button>
      </div>

      <Section title="Upcoming" empty="No upcoming events.">
        {upcoming.map((ev) => (
          <EventRow key={ev.event_id} ev={ev} onEdit={() => open(ev)} onDelete={() => remove(ev)} />
        ))}
      </Section>

      <Section title="Replays & Past" empty="No replays.">
        {replays.map((ev) => (
          <EventRow key={ev.event_id} ev={ev} onEdit={() => open(ev)} onDelete={() => remove(ev)} replay />
        ))}
      </Section>

      <Dialog open={showForm} onOpenChange={(v) => { if (!v) { setShowForm(false); setEditingId(null); } }}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 max-w-lg" data-testid="event-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">
              {editingId ? "Edit event" : "New event"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4 mt-2">
            <div>
              <Label className="text-xs text-white/60">Title</Label>
              <Input value={form.title} onChange={(e) => setForm({...form, title: e.target.value})}
                required data-testid="event-title"
                className="bg-[#0F0F0F] border-white/10 mt-1"/>
            </div>
            <div>
              <Label className="text-xs text-white/60">Description</Label>
              <Textarea value={form.description} onChange={(e) => setForm({...form, description: e.target.value})}
                rows={3} data-testid="event-description"
                className="bg-[#0F0F0F] border-white/10 mt-1"/>
            </div>
            <div>
              <Label className="text-xs text-white/60">Date & time</Label>
              <Input type="datetime-local" value={form.starts_at}
                onChange={(e) => setForm({...form, starts_at: e.target.value})}
                required data-testid="event-starts-at"
                className="bg-[#0F0F0F] border-white/10 mt-1"/>
            </div>
            <div>
              <Label className="text-xs text-white/60">Zoom / Vimeo / video URL</Label>
              <Input value={form.zoom_url} onChange={(e) => setForm({...form, zoom_url: e.target.value})}
                placeholder="https://zoom.us/j/… or https://vimeo.com/…"
                data-testid="event-url"
                className="bg-[#0F0F0F] border-white/10 mt-1"/>
            </div>
            <div className="flex items-center gap-3">
              <Switch checked={form.is_replay} onCheckedChange={(v) => setForm({...form, is_replay: v})}
                data-testid="event-replay-toggle" />
              <div>
                <div className="text-sm">Replay (past recording)</div>
                <div className="text-[11px] text-white/40">Toggle on for videos already recorded.</div>
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" onClick={() => { setShowForm(false); setEditingId(null); }}
                variant="outline"
                className="rounded-full bg-transparent border-white/10">Cancel</Button>
              <Button type="submit" data-testid="event-submit"
                className="rounded-full bg-gradient-to-r from-[#FF8A00] to-[#E52E71]">
                {editingId ? "Save" : "Create"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Section({ title, empty, children }) {
  const hasKids = React.Children.count(children) > 0;
  return (
    <div>
      <div className="text-[11px] tracking-[0.22em] uppercase text-[#FF8A00] mb-3">{title}</div>
      <div className="space-y-2">
        {hasKids ? children : <div className="text-sm text-white/40 px-4 py-3 bg-[#0A0A0A] border border-white/5 rounded-xl">{empty}</div>}
      </div>
    </div>
  );
}

function EventRow({ ev, onEdit, onDelete, replay }) {
  return (
    <div data-testid={`event-row-${ev.event_id}`}
      className="flex flex-wrap items-center gap-4 p-4 bg-[#0A0A0A] border border-white/5 rounded-xl">
      <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#FF8A00]/20 to-[#E52E71]/10 flex items-center justify-center">
        {replay ? <PlayCircle size={18} className="text-[#FF8A00]" /> : <Video size={18} className="text-[#FF8A00]" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm truncate">{ev.title}</div>
        <div className="text-[11px] text-white/40 mt-0.5 flex items-center gap-2">
          <CalendarDays size={11} /> {fmt(ev.starts_at)}
          {ev.zoom_url && <a href={ev.zoom_url} target="_blank" rel="noreferrer"
            className="underline truncate max-w-[220px] hover:text-white">{ev.zoom_url}</a>}
        </div>
      </div>
      <button data-testid={`edit-event-${ev.event_id}`} onClick={onEdit}
        className="text-white/40 hover:text-white p-2">
        <Pencil size={14} />
      </button>
      <button data-testid={`delete-event-${ev.event_id}`} onClick={onDelete}
        className="text-white/40 hover:text-[#E52E71] p-2">
        <Trash2 size={14} />
      </button>
    </div>
  );
}

function blank() {
  const dt = new Date(Date.now() + 7 * 24 * 3600 * 1000);
  return {
    title: "", description: "",
    starts_at: toLocalInput(dt.toISOString()),
    zoom_url: "", is_replay: false,
  };
}

function toLocalInput(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch { return ""; }
}

function fmt(iso) {
  try {
    return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
  } catch { return iso; }
}
